import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';
import { SupportAttachments, AttachmentList } from './SupportAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'open', label: 'Open', bg: '#dbeafe', fg: '#1e40af' },
  { key: 'in_progress', label: 'In Progress', bg: '#fef3c7', fg: '#92400e' },
  { key: 'awaiting_member', label: 'Awaiting Member', bg: '#fed7aa', fg: '#9a3412' },
  { key: 'resolved', label: 'Resolved', bg: '#d1fae5', fg: '#065f46' },
  { key: 'closed', label: 'Closed', bg: '#f3f4f6', fg: '#374151' },
];

const AdminSupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const [assigneeInput, setAssigneeInput] = useState('');

  const authHeaders = useCallback(() => ({
    withCredentials: true,
    headers: { Authorization: `Bearer ${getToken()}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/support/tickets`, authHeaders());
      setTickets(res.data || []);
    } catch (e) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter(t => {
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (q) {
        const blob = [t.subject, t.member_name, t.member_email, t.ticket_id, t.message].join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, filterStatus, search]);

  const counts = useMemo(() => {
    const c = { total: tickets.length, open: 0, in_progress: 0, awaiting_member: 0, resolved: 0, closed: 0 };
    tickets.forEach(t => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tickets]);

  const updateStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticketId}`, { status }, authHeaders());
      await load();
    } catch (e) {
      alert('Could not update status: ' + (e.response?.data?.detail || e.message));
    }
  };

  const updateAssignee = async (ticketId, assigned_to) => {
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticketId}`, { assigned_to }, authHeaders());
      setAssigneeInput('');
      await load();
    } catch (e) {
      alert('Could not update assignee: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sendReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setSavingReply(true);
    try {
      await axios.post(`${API}/admin/support/tickets/${ticketId}/reply`, { message: replyText, attachments: replyFiles }, authHeaders());
      setReplyText('');
      setReplyFiles([]);
      await load();
    } catch (e) {
      alert('Reply failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingReply(false);
    }
  };

  const StatusBadge = ({ s }) => {
    const c = STATUSES.find(x => x.key === s) || { label: s, bg: '#e5e7eb', fg: '#374151' };
    return <span style={{ background: c.bg, color: c.fg, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{c.label}</span>;
  };

  return (
    <div data-testid="admin-support-tab" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>Support Tickets</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[['total', 'All', counts.total], ['open', 'Open', counts.open], ['in_progress', 'In Progress', counts.in_progress], ['awaiting_member', 'Awaiting Member', counts.awaiting_member], ['resolved', 'Resolved', counts.resolved], ['closed', 'Closed', counts.closed]].map(([k, label, n]) => (
          <div key={k} style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer' }}
            onClick={() => setFilterStatus(k === 'total' ? 'all' : k)}>
            <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>{n}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search subject, member, email…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 240px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
          data-testid="support-search" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select" data-testid="support-status-filter">
          <option value="all">All statuses</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading tickets…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No tickets match.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(t => {
            const isOpen = expanded === t.ticket_id;
            return (
              <div key={t.ticket_id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>{t.subject}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {t.member_name || '—'} · {t.member_email || '—'} · UID {t.user_id?.slice(-6)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                      #{t.ticket_id.slice(-6)} · opened {new Date(t.created_at).toLocaleDateString('en-AU')} · updated {new Date(t.updated_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                      {t.assigned_to ? ` · assigned ${t.assigned_to}` : ''}
                    </div>
                  </div>
                  <StatusBadge s={t.status} />
                  <select value={t.status} onChange={e => updateStatus(t.ticket_id, e.target.value)} style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px' }} data-testid={`status-select-${t.ticket_id}`}>
                    {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  <button className="btn btn-sm" onClick={() => { setExpanded(isOpen ? null : t.ticket_id); setAssigneeInput(t.assigned_to || ''); setReplyText(''); setReplyFiles([]); }}>
                    {isOpen ? 'Collapse' : 'Open'}
                  </button>
                </div>

                {isOpen && (
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ background: '#f9fafb', padding: '10px 14px', borderRadius: '6px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{t.member_name || 'Member'} · {new Date(t.created_at).toLocaleString('en-AU')}</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{t.message}</div>
                      <AttachmentList attachments={t.attachments} />
                    </div>
                    {(t.replies || []).map(r => (
                      <div key={r.reply_id} style={{ background: r.author === 'admin' ? '#eff6ff' : '#f9fafb', padding: '10px 14px', borderRadius: '6px', marginBottom: '10px' }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          {r.author === 'admin' ? `${r.author_name || 'Admin'} (You)` : (r.author_name || 'Member')} · {new Date(r.created_at).toLocaleString('en-AU')}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{r.message}</div>
                        <AttachmentList attachments={r.attachments} />
                      </div>
                    ))}

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '13px', color: '#374151' }}>Assigned to:</label>
                      <input value={assigneeInput} onChange={e => setAssigneeInput(e.target.value)} placeholder="e.g. Peter"
                        style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', flex: '1 1 180px' }} />
                      <button className="btn btn-sm" onClick={() => updateAssignee(t.ticket_id, assigneeInput)}>Save</button>
                    </div>

                    <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={4} placeholder="Reply to the member… (emails them when sent)"
                      style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit' }}
                      data-testid={`admin-reply-input-${t.ticket_id}`} />
                    <SupportAttachments attachments={replyFiles} setAttachments={setReplyFiles} testId={`admin-reply-attach-${t.ticket_id}`} />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => sendReply(t.ticket_id)} disabled={savingReply || !replyText.trim()}>
                        {savingReply ? 'Sending…' : 'Send Reply & Email'}
                      </button>
                      <button className="btn btn-sm" onClick={() => updateStatus(t.ticket_id, 'resolved')}>Mark Resolved</button>
                      <button className="btn btn-sm" onClick={() => updateStatus(t.ticket_id, 'closed')}>Close</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSupportTab;
